/**
 * 古いスラッグの一覧と転送の ON / OFF（2026-09-28 creator/slug-rename）。親は SlugSettings.tsx（送信・再認証・通知は親）。
 * サーバー: GET …/slug の oldSlugs・PATCH …/slug/old/:oldSlug { redirect }（再認証あり）。
 *
 * 決めたこと:
 *   - 各行に「転送中（月 $1）」/「見つかりません」を出す。料金は ON の行にだけ出す（OFF は掛からない）
 *   - ON にするときは料金の確認（月額と日割り）、OFF にするときは「古いリンクは見つかりませんになる・またいつでも ON にできる」の確認
 *     WHY 両方とも確認を挟むか: ON はお金が掛かり、OFF はプレイヤーの持っているリンクが切れる。どちらも押し間違いを避けたい
 *   - 古いスラッグは本人のために取っておかれる（誰も取れない）ことを一覧の上に書く = OFF にしても名前を失わないと分かるように
 */
import { formatDate, useT } from '../i18n';
import { urlRows, type OldSlug } from '../slugRename';
import { usd } from './SlugChangeDialog';
import { ConfirmDialog } from './ui';

export interface RedirectToggle {
  oldSlug: string;
  on: boolean;
}

export function OldSlugsList({
  oldSlugs,
  price,
  daily,
  busy,
  toggle,
  toggleError,
  onAsk,
  onConfirm,
  onCancel
}: {
  oldSlugs: OldSlug[];
  /** 転送 1 つの月額（USD）。null = サーバーが返さない（金額なしの文言にする） */
  price: number | null;
  daily: boolean;
  busy: boolean;
  /** 確認を開いている切り替え（null = 閉じている） */
  toggle: RedirectToggle | null;
  toggleError: string | null;
  onAsk: (t: RedirectToggle) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  if (!oldSlugs.length && !toggle) return null;
  const onCost = price === null ? t('slug.choice.keepCostUnknown') : t(daily ? 'slug.choice.keepCost' : 'slug.choice.keepCostMonthly', { price: usd(price) });

  return (
    <div className="slug-old">
      <h3 className="slug-old-title">{t('slug.old.title')}</h3>
      <p className="small muted">{t('slug.old.lead')}</p>
      <ul className="slug-old-list">
        {oldSlugs.map((o) => (
          <li key={o.slug} className="slug-old-row">
            <div className="slug-old-head">
              <strong className="mono">{o.slug}</strong>
              <span className={`pill ${o.redirect ? 'pill-ok' : ''}`}>{t(o.redirect ? 'slug.old.redirecting' : 'slug.old.notFound')}</span>
              {o.redirect && price !== null && <span className="pill">{t('slug.old.perMonth', { price: usd(price) })}</span>}
              <span className="slug-old-actions">
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onAsk({ oldSlug: o.slug, on: !o.redirect })}>
                  {t(o.redirect ? 'slug.old.turnOff' : 'slug.old.turnOn')}
                </button>
              </span>
            </div>
            <p className="small muted slug-old-meta">
              {t('slug.old.retired', { date: formatDate(o.retiredAt) })}
              {urlRows(o.urls).map((u) => (
                <span key={u.url} className="mono">
                  {' · '}
                  {u.url}
                </span>
              ))}
            </p>
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={!!toggle}
        title={toggle ? t(toggle.on ? 'slug.old.onTitle' : 'slug.old.offTitle', { old: toggle.oldSlug }) : ''}
        confirmLabel={toggle ? t(toggle.on ? 'slug.old.turnOn' : 'slug.old.turnOff') : ''}
        // OFF はプレイヤーのリンクが切れる = 危険の色（Cancel にフォーカス）
        tone={toggle && !toggle.on ? 'danger' : 'default'}
        busy={busy}
        onClose={onCancel}
        onConfirm={onConfirm}
      >
        {toggle && <p>{toggle.on ? onCost : t('slug.old.offBody', { old: toggle.oldSlug })}</p>}
        <p className="small muted">{t('slug.confirm.reauth')}</p>
        {toggleError && (
          <p className="alert alert-error" role="alert">
            {toggleError}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
